import React, { useState, useRef } from 'react';
import { useRestoMod } from '../../context/RestoModContext';
import { VisualizerState } from '../../types/vehicle';
import { DEFAULT_VISUALIZER } from '../../context/defaultData';
import { StanceCanvas, StanceCanvasRef } from './StanceCanvas';
import { SilhouettePicker } from './SilhouettePicker';
import { PaintRack } from './PaintRack';
import { WheelBay } from './WheelBay';
import { SuspensionRack } from './SuspensionRack';
import { StanceToolbar } from './StanceToolbar';
import { TactilePanel } from '../common/TactilePanel';
import {
  Car,
  Sliders,
  Disc,
  Palette,
  Eye,
} from 'lucide-react';

export type VisualizerTab = 'silhouette' | 'suspension' | 'wheels' | 'paint';

export const VisualizerView: React.FC = () => {
  const { state, dispatch } = useRestoMod();
  const { visualizer } = state;
  const [activeTab, setActiveTab] = useState<VisualizerTab>('suspension');
  const canvasRef = useRef<StanceCanvasRef | null>(null);

  const handleUpdateVisualizer = (updates: Partial<VisualizerState>) => {
    dispatch({
      type: 'UPDATE_VISUALIZER',
      payload: updates,
    });
  };

  const handleLoadPreset = (preset: VisualizerState) => {
    dispatch({
      type: 'UPDATE_VISUALIZER',
      payload: preset,
    });
  };

  const handleResetDefaults = () => {
    dispatch({
      type: 'UPDATE_VISUALIZER',
      payload: DEFAULT_VISUALIZER,
    });
  };

  const handleExportSnapshot = () => {
    canvasRef.current?.exportSnapshot();
  };

  const tabs: {
    id: VisualizerTab;
    label: string;
    icon: React.ReactNode;
    badge?: string;
  }[] = [
    {
      id: 'suspension',
      label: 'Stance & Rake',
      icon: <Sliders className="w-4 h-4 text-safety-amber" />,
      badge: `${visualizer.stance.frontHeightOffsetInches.toFixed(1)}" / ${visualizer.stance.rearHeightOffsetInches.toFixed(1)}"`,
    },
    {
      id: 'wheels',
      label: 'Wheels & Tires',
      icon: <Disc className="w-4 h-4 text-safety-cyan" />,
      badge: `${visualizer.stance.frontWheelDiameter}" ${visualizer.wheel.archetype.replace('_', ' ')}`,
    },
    {
      id: 'paint',
      label: 'Paint Rack',
      icon: <Palette className="w-4 h-4 text-safety-amber" />,
      badge: visualizer.paint.presetName || visualizer.paint.finishType.replace('_', ' '),
    },
    {
      id: 'silhouette',
      label: 'Silhouette & CAD',
      icon: <Car className="w-4 h-4 text-shop-muted" />,
      badge: visualizer.silhouetteId.replace('_', ' '),
    },
  ];

  return (
    <div className="space-y-6">
      {/* 1. Master Canvas Viewport Panel */}
      <TactilePanel
        title="3D WebGL Vehicle Stance, Wheel & Paint Studio"
        subtitle="Interactive 3D WebGL PBR automotive visualizer with real-time suspension stance kinematics, vintage coatings, and CAD turntable"
        badge={
          <span className="p-1.5 bg-shop-darkest rounded border border-shop-border text-safety-amber">
            <Eye className="w-4 h-4" />
          </span>
        }
        action={
          <span className="text-xs font-mono font-bold text-blueprint-cyan bg-shop-darkest px-2.5 py-1 rounded border border-shop-border">
            3D WEBGL ENGINE (THREE.JS)
          </span>
        }
      >
        <div className="space-y-3">
          {/* Stance Preset & Actions Toolbar */}
          <StanceToolbar
            state={visualizer}
            onLoadPreset={handleLoadPreset}
            onUpdateState={handleUpdateVisualizer}
            onResetDefaults={handleResetDefaults}
            onExportSnapshot={handleExportSnapshot}
          />

          {/* Canvas Viewport */}
          <StanceCanvas
            ref={canvasRef}
            state={visualizer}
            onUpdateState={handleUpdateVisualizer}
            allowPinning={true}
          />
        </div>
      </TactilePanel>

      {/* 2. Tool Station: Navigation Tabs & Control Drawers */}
      <TactilePanel
        title="Workshop Tuning Stations"
        subtitle="Select an engineering station below to adjust vehicle geometry, suspension, rolling stock, and coatings"
      >
        <div className="space-y-6">
          {/* Station Navigation Tab Bar (Min 48px touch targets) */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 border-b border-shop-border pb-4">
            {tabs.map((tab) => {
              const isCurrent = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`min-h-[48px] px-3.5 py-2.5 rounded border text-left flex items-center justify-between gap-2 transition-all duration-100 cursor-pointer ${
                    isCurrent
                      ? 'bg-shop-panel text-shop-highlight border-safety-amber shadow-[inset_0_2px_0_0_#f59e0b]'
                      : 'bg-shop-dark text-shop-muted border-shop-border hover:bg-shop-well hover:text-shop-light'
                  }`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="shrink-0">{tab.icon}</span>
                    <span className="text-xs font-bold uppercase tracking-wider truncate">
                      {tab.label}
                    </span>
                  </div>
                  {tab.badge && (
                    <span className="hidden md:inline text-[10px] font-mono text-shop-muted px-1.5 py-0.5 rounded bg-shop-darkest border border-shop-border truncate shrink-0 max-w-[100px]">
                      {tab.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Station Control Drawers */}
          <div className="min-h-[300px]">
            {activeTab === 'suspension' && (
              <SuspensionRack
                stance={visualizer.stance}
                silhouetteId={visualizer.silhouetteId}
                onChangeStance={(stanceUpdates) =>
                  handleUpdateVisualizer({
                    stance: { ...visualizer.stance, ...stanceUpdates },
                  })
                }
              />
            )}

            {activeTab === 'wheels' && (
              <WheelBay
                wheel={visualizer.wheel}
                stance={visualizer.stance}
                onChangeWheel={(wheelUpdates) =>
                  handleUpdateVisualizer({
                    wheel: { ...visualizer.wheel, ...wheelUpdates },
                  })
                }
                onChangeStance={(stanceUpdates) =>
                  handleUpdateVisualizer({
                    stance: { ...visualizer.stance, ...stanceUpdates },
                  })
                }
              />
            )}

            {activeTab === 'paint' && (
              <PaintRack
                paint={visualizer.paint}
                onChangePaint={(paintUpdates) =>
                  handleUpdateVisualizer({
                    paint: { ...visualizer.paint, ...paintUpdates },
                  })
                }
              />
            )}

            {activeTab === 'silhouette' && (
              <SilhouettePicker
                selectedId={visualizer.silhouetteId}
                customSilhouetteDataUrl={visualizer.customSilhouetteDataUrl}
                onSelectSilhouette={(id) => handleUpdateVisualizer({ silhouetteId: id })}
                onUploadCustomImage={(dataUrl) =>
                  handleUpdateVisualizer({
                    silhouetteId: 'custom_upload',
                    customSilhouetteDataUrl: dataUrl,
                  })
                }
              />
            )}
          </div>
        </div>
      </TactilePanel>
    </div>
  );
};
